import React, { useEffect, useState } from "react";
import axios from "axios";
import { useForm } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import toastr from "../../../utils/toast";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function AddMonthlyRevenueTarget({employeeId, employeeName, onClose, onSuccess}) {
  const { token, setGlobalLoading } = useOutletContext();
  const {register, handleSubmit, reset, setValue, watch, formState: { errors }} = useForm();
  const selectedMonth = watch("month");
  const [financialYear, setFinancialYear] = useState("");
  const [loadingTarget, setLoadingTarget] = useState(false);
  const [existingTarget, setExistingTarget] = useState(false);

  const getCurrentFinancialYear = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;
    const startYear = currentMonth >= 4 ? currentYear : currentYear - 1;
    const endYear = startYear + 1;
    return `${startYear}-${endYear}`;
  };

  useEffect(() => {
    const currentFY = getCurrentFinancialYear();
    setFinancialYear(currentFY);
    reset({month: "", target: ""});
    setExistingTarget(false);
  }, [reset]);

  const months = [
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
  ];

  useEffect(() => {
    const fetchMonthlyTarget = async () => {
      if (!selectedMonth || !employeeId || !financialYear) {
        setExistingTarget(false);
        setValue("target", "");
        return;
      }

      try {
        setLoadingTarget(true);
        const response = await axios.get(
          `${API_BASE_URL}/invoices/get_monthly_revenue_targets`,
          {
            params: {
              employeeinfo_id: employeeId,
              month: Number(selectedMonth),
              year: financialYear,
            },

            headers: {Authorization: `Bearer ${token}`},
          }
        );

        const targetRecord = response?.data?.data;
        if (targetRecord) {
          setExistingTarget(true);
          setValue("target", targetRecord.target);
        } else {
          setExistingTarget(false);
          setValue("target", "");
        }
      } catch (error) {
        console.error("Fetch monthly target error:", error);
        setExistingTarget(false);
        setValue("target", "");
        toastr.error("Unable to fetch monthly target");
      } finally {
        setLoadingTarget(false);
      }
    };

    fetchMonthlyTarget() ;
  }, [selectedMonth, employeeId, financialYear, token, setValue]);


  const onSubmit = async (formData) => {
    try {
      setGlobalLoading?.(true);
      const payload = {
        employeeinfo_id: employeeId,
        month: Number(formData.month),
        target: Number(formData.target),
        year: financialYear,
        target_type: "delivered",
      };

      await axios.post(
        `${API_BASE_URL}/invoices/monthly_revenue_targets`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      toastr.success(
        existingTarget
          ? "Monthly revenue target updated successfully"
          : "Monthly revenue target added successfully"
      );

      onSuccess?.();

    } catch (error) {
      console.error(
        "Add monthly target error:",
        error
      );

      toastr.error(
        error?.response?.data?.message ||
          "Unable to save monthly revenue target"
      );

    } finally {
      setGlobalLoading?.(false);
    }
  };

  return (
    <div
      className="modal modal-sm fade show"
      style={{
        display: "block",
        backgroundColor:
          "rgba(0,0,0,0.5)",
      }}
      tabIndex="-1"
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          {/* HEADER */}
          <div className="modal-header">
            <h5 className="modal-title">
              {existingTarget
                ? "Update Monthly Revenue Target"
                : "Add Monthly Revenue Target"}
            </h5>

            <button
              type="button"
              className="btn-close"
              onClick={onClose}
            ></button>
          </div>

          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label">Employee</label>
                <input type="text" className="form-control" value={employeeName || ""} disabled />
              </div>

              <div className="mb-3">
                <label className="form-label">Financial Year</label>
                <input type="text" className="form-control" value={financialYear} disabled />
              </div>

              <div className="mb-3">
                <label className="form-label">
                  Month{" "}<span className="text-danger">*</span>
                </label>
                <select className={`form-select ${errors.month ? "is-invalid" : ""}`}
                  {...register("month", {required: "Please select month"})}
                >
                  <option value="">Select Month</option>
                  {months.map((month) => (
                    <option
                      key={month.value}
                      value={month.value}
                    >
                      {month.label}
                    </option>
                  ))}
                </select>

                {errors.month && (
                  <div className="invalid-feedback">
                    {errors.month.message}
                  </div>
                )}
              </div>

              {/* TARGET */}

              <div className="mb-3">
                <label className="form-label">
                  Target Amount{" "}
                  <span className="text-danger">
                    *
                  </span>
                </label>

                <input
                  type="number"
                  className={`form-control ${
                    errors.target
                      ? "is-invalid"
                      : ""
                  }`}
                  placeholder="Enter target amount"
                  min="0"
                  disabled={loadingTarget}
                  {...register("target", {
                    required:
                      "Please enter target amount",

                    min: {
                      value: 1,
                      message:
                        "Target must be greater than 0",
                    },
                  })}
                />

                {loadingTarget && (
                  <small className="text-muted">
                    Loading existing target...
                  </small>
                )}

                {existingTarget &&
                  !loadingTarget && (
                    <small className="text-success">
                      Existing target loaded. You
                      can update the amount.
                    </small>
                  )}

                {errors.target && (
                  <div className="invalid-feedback">
                    {errors.target.message}
                  </div>
                )}
              </div>
            </div>

            {/* FOOTER */}

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-light me-2"
                onClick={onClose}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={loadingTarget}
              >
                {existingTarget
                  ? "Update Target"
                  : "Save Target"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AddMonthlyRevenueTarget;